import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    Box,
    Grid,
    CircularProgress,
} from '@material-ui/core';
import {
    SentimentVerySatisfied,
    SentimentVeryDissatisfied
} from '@material-ui/icons';
import StepperIcons from "./StepperIcons";
import ContactForm from "./Forms/ContactForm";
import DonationForm from "./Forms/DonationForm";
import PaymentForm from "./Forms/PaymentForm";
import ServiceForm from "./Forms/ServiceForm";
import {
    useStripe,
    useElements,
    CardCvcElement,
} from '@stripe/react-stripe-js';
import { useStateValue } from "../StateContext";
import StepConnector from './StepConnector'
import {
    clientSecretPull,
    stripeDataObjectConverter,
    clientSecretDataObjectConverter,
    stripePaymentMethodHandler
} from '../constants/functions';

// OVERALL STYLE
const style = makeStyles(theme => ({
    button: {
        marginRight: theme.spacing(1),
    },
    mainBox: {
        position: "relative",
        marginTop: "-8px",
        padding: "10px 20px",
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px",
        background: theme.palette.background.default
    },
    stepper: {
        height: "calc(10vh - 40px)",
        minHeight: "55px"
    },
    form: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around"
    },
    buttonWrapper: {
        justifyContent: "flex-end"
    },
}));

const StepContent = ({ step }) => {
    switch (step) {
        case 0:
            return <ContactForm />;
        case 1:
            return <ServiceForm />;
        case 2:
            return <DonationForm />;
        case 3:
            return <PaymentForm />;
        default:
            return <></>;
    }
}

const Steppers = () => {
    const classes = style();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [cardStatus, setCardStatus] = useState(true);
    const [cardMessage, setCardMessage] = useState("");

    const stripe = useStripe();
    const elements = useElements();
    const [{ formValues }, dispatch] = useStateValue();

    const handleNext = () => {
        if (activeStep === 3) {
            capture()
        } else {
            setActiveStep(prevActiveStep => prevActiveStep + 1);
        }
    };
    const handleBack = () => { setActiveStep((prevActiveStep) => prevActiveStep - 1); setLoading(false) };
    const handleReset = () => setActiveStep(0);

    const capture = async () => {

        setLoading(true);

        const cardElement = elements.getElement(CardCvcElement);

        if (formValues.recurring) {
            //MONTHLY PAYMENTS
            const result = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    email: formValues.email,
                },
            });

            if (result.error) {
                setCardStatus(false);
                setCardMessage(`Oops... ${result.error.message}`);
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
                return;
            } else {
                const { error, latest_invoice } = await stripePaymentMethodHandler(result, formValues);
                if (error) {
                    // Handle payment error
                    setCardStatus(false);
                    setCardMessage(`Oops... ${error.message}`)
                } else if (latest_invoice.payment_intent && latest_invoice.payment_intent.status === "succeeded") {
                    // Handle payment success
                    setCardStatus(true);
                    setCardMessage("Thank you for your donation and monthly payment.");
                    dispatch({ type: 'emptyFormValue' });
                }
            }
        } else {
            //SINGLE PAYMENTS
            const clientSecretDataObject = clientSecretDataObjectConverter(formValues);
            const clientResult = await clientSecretPull(clientSecretDataObject);

            if ("error" in clientResult) {
                setCardStatus(false);
                setCardMessage(`Oops... ${clientResult.error}`);
            } else {

                const stripeDataObject = stripeDataObjectConverter(formValues, cardElement);
                const { error, paymentIntent } = await stripe.confirmCardPayment(clientResult.clientSecret, stripeDataObject);
                if (error) {
                    // Handle payment error
                    setCardStatus(false);
                    setCardMessage(`Oops... ${error.message}`)
                } else if (paymentIntent && paymentIntent.status === "succeeded") {
                    // Handle payment success
                    setCardStatus(true);
                    setCardMessage("Thank you for your donation.");
                    dispatch({ type: 'emptyFormValue' });
                }
            }
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setLoading(false);
    }

    return (
        <>
            <Stepper alternativeLabel activeStep={activeStep} connector={<StepConnector />} className={classes.stepper}>
                {[1, 2, 3, 4].map(e => (
                    <Step key={e}>
                        <StepLabel StepIconComponent={StepperIcons} />
                    </Step>
                ))}
            </Stepper>
            <Box className={classes.mainBox}>
                {activeStep === 4 ?
                    <Grid
                        container
                        spacing={3}
                        direction="column"
                        justify="space-around"
                        alignItems="center"
                        style={{ height: "400px" }}
                    >
                        {cardStatus ?
                            <SentimentVerySatisfied fontSize="large" color="primary" />
                            :
                            <SentimentVeryDissatisfied fontSize="large" color="error" />
                        }
                        <Typography style={{ textAlign: "center" }} variant="h4">
                            {cardMessage}
                        </Typography>
                        <Button onClick={cardStatus ? handleReset : handleBack} className={classes.button}>
                            {cardStatus ? "Reset" : "Back"}
                        </Button>
                    </Grid>
                    :
                    <form className={classes.form} onSubmit={e => { e.preventDefault(); handleNext() }}>
                        <Grid container spacing={3}>
                            <StepContent step={activeStep} />
                            <Grid container item className={classes.buttonWrapper}>
                                <Button disabled={activeStep === 0} onClick={handleBack} className={classes.button}>
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.button}
                                    type="submit"
                                    disabled={loading}
                                >
                                    {
                                        loading
                                            ?
                                            <CircularProgress size={24} />
                                            :
                                            activeStep === 3 ? 'Pay' : 'Next'
                                    }
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                }
            </Box>
        </>
    );
}

export default Steppers;